import {Map as IMap} from 'immutable';
import {
  Plugins,
  Component,
  Expressions,
  Types,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {Err, Ok, either, type Result} from '@packages/utilities/fp/result';
import {
  DynListLimitsConfig,
  countDynamoWrappers,
  limitCollectionListCount,
  limitNestedCollectionListCount,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {
  getConstraintsForElement,
  validateComponentDefinitionConstraintChanges,
  validateComponentMapConstraints,
  validatePageConstraints,
} from '@packages/systems/constraints';

import {
  contains,
  pageComponentType,
  getComponentSlots,
  isUserComponentInst,
  getDisplayNameForType,
  addElementAtAnchor,
  removeElementById,
  isComponent,
  isUserComponent,
  isComponentOnlyPageSlotDescendant,
  type RelativePositionPlacementOptions,
  isComponentInst,
  expressionContainsSpecificElement,
} from '@packages/utilities/platform';
import {validateCampaignBuildModePlacement} from './CampaignBuildModePlacement';
import {
  isDynamicBoundOrConditioned,
  isDivBlock,
  isListItem,
  isList,
  isDropdownList,
  isDropdownToggle,
  isDropdownWrapper,
  isCell,
  isColumn,
  isLayout,
  isRow,
  isGrid,
  isHFlex,
  isVFlex,
  isSliderWrapper,
  isSliderMask,
  isSliderSlide,
  isTabsWrapper,
  isTabsContent,
  isTabsPane,
  isBlockContainer,
  isContainer,
  isNavbarWrapper,
  isNavbarContainer,
  isNavbarMenu,
  isDOM,
  isComponentOnlyDropTarget,
  isDynamoWrapper,
  isRichText,
  isSlot,
} from '@packages/utilities/elementTypeUtils';
import {isSection} from '@packages/utilities/platform-elements';
import {isCodeIsland} from '@packages/utilities/core-elements';

import * as DesignerErrorCodes from '@packages/systems/core/constants/ErrorCodes';

import {EElement, type ExpressionElement} from '@packages/systems/wfdl-ast';
import {CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import {ComponentInstanceCounts} from '@packages/systems/core/records/DesignerState';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {isDynPage, isStaticUtilityPage} from '@packages/shared/utils/PageUtils';
import {validateDynamicContext as internalValidateDynamicContext} from '@packages/systems/dynamo/gateway';
import {validateLocalesContext} from '@packages/systems/plugins/Localization';
import {
  componentInstanceCountsByPage,
  getAncestorComponents,
} from '@packages/systems/user-components/utils';
import {validateNoCircularComponents} from '@packages/utilities/platform-validation';
import {getName, namesEqual} from '@packages/systems/wfdl-Qualified';

import {
  UserComponentUsageMap,
  detectConstrainingUserComponents,
} from '@packages/systems/user-components/usages';
import {validateNesting} from './ValidateNesting';
import {validateCmsRtfNesting} from './cmsRtfValidation';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {
  isOrContainsStaleUserComponent,
  canUseComponentAndDescendantsInPageBuildMode,
} from '@packages/utilities/PageBuildUtils';
import {
  designerContent,
  designerTranslation,
} from '@packages/systems/designer-translations';
import {
  getSlotOverrideFieldId,
  getComponentBySlotElementId,
  getSlotRestrictions,
  isComponentAllowedInSlot,
} from '@packages/systems/component-slots';

export const getErrorMessage = either<string, string | undefined>(
  (msg: string) => msg
)(() => undefined);

export const validateComponentMapCycles = (
  componentMap: Component.ComponentMap,
  messageGetter: DesignerErrorCodes.MessageGetter
): Result<string, Component.ComponentMap> =>
  DesignerErrorCodes.errorCodeToMessage(messageGetter)(
    validateNoCircularComponents(componentMap)
  );

export type RuntimeArgs =
  | {
      runtimeEnvironment: typeof WebflowEnvironments.DESIGNER;
      isInPageBuildMode: boolean;
      isInCampaignBuildMode: boolean;
      staleComponents: {[key: string]: boolean};
      canAddComponentsInStaticRichText?: boolean;
    }
  | {runtimeEnvironment: typeof WebflowEnvironments.SSR}
  | {runtimeEnvironment: typeof WebflowEnvironments.EDITOR};

export type ValidationContext = {
  collections: CollectionsType;
  componentInstanceCounts: ComponentInstanceCounts;
  plugins: Plugins.PluginsType;
  currentPageId: string;
  userComponentUsageMap: UserComponentUsageMap;
  cmsRtfComponentIds?: readonly string[];
  currentComponentType: Plugins.QualifiedElement;
  pages: IMap<string, PageRecordType>;
  // Parent element of the child. Raw means that it is NOT expanded.
  // Expanded element is one where all user components instances are replaced with their roots without applying overrides
  rawParent: ExpressionElement;
  // The element that was either added, dragged or otherwise modified. Raw means that it is NOT expanded.
  // Expanded element is one where all user components instances are replaced with their roots without applying overrides
  rawChild: ExpressionElement;
  // When the drop/paste targets a Slot inside a component instance, the path to
  // that Slot's SlotContent field on the instance. Identifies exactly which slot
  // is being filled; without it the only signal is rawParent, which is the
  // instance and does not say which of its slots was targeted.
  anchorPath?: ExpressionPath | null;
  // Whether the element is new or not. For example, when dragging and dropping an existing element, it is not new.
  isNewElement: boolean;
  // Source data is required when child element is potentially from a different state.
  // For example, when copy pasting, the source data is the data from when the element was copied.
  // It's not needed when there can not be other changes in between. For example, when dragging and dropping.
  sourceData?: SourceData;
  // Which environment the validation is running in. For example, designer, editor, ssr
  runtimeArgs: RuntimeArgs;
  // As the ValidationUtils is ran both on the server (MP-server, for example) and on the client (designer), we need to pass feature flags.
  // This is a temporary hack to get around the fact that the generic feature flag package is not yet working correctly.
  // TODO: Remove this once the feature flag package is working correctly.
  // See for reference: https://webflow.slack.com/archives/C07NJ61T056/p1763668919329389?thread_ts=1763403043.571549&cid=C07NJ61T056
  featureFlags: {
    isComponentSlotDefaultsEnabled?: boolean;
  };
  /**
   * The plugin ids of the site's installed libraries.
   *
   * A slot restriction only ever names a site component or a library one --
   * those are the only things the picker offers -- so only those can be
   * enforced against. A core plugin's components can never appear in an
   * allow-list, and treating them as candidates would refuse them from every
   * restricted slot with no way for an author to permit them.
   *
   * Absent means no plugin component is a candidate.
   */
  libraryPluginIds?: ReadonlySet<string>;
  // Evaluate and pass in collection list limits from the statsig dc-cmsext-ncl-limits config. This should be
  // set when in a server context to ensure the dynamic list limits are correctly enforced. Our validation logic getDynListLimits() uses
  // the client Statsig client to evaluate the config, which is not available in a server context.
  // TODO: Remove this once the feature flag package is working correctly.
  dynListLimits?: DynListLimitsConfig;
  // Fires only when a slot restriction actually blocks this change -- callers
  // that don't care about attempt analytics (e.g. multiplayer server sync)
  // can simply omit it.
  onSlotRestrictionBlocked?: (ctx: {
    parentId: string;
    slotId: string;
    attemptedComponentId: string;
  }) => void;
};

type SourceData = {
  componentMap: Component.ComponentMap;
  currentPageId: string;
  currentComponentType: Plugins.QualifiedElement;
  collections: CollectionsType;
  pages: IMap<string, PageRecordType>;
};

const hasPlacementConstraints =
  (componentMap: Component.ComponentMap, plugins: Plugins.PluginsType) =>
  (element: ExpressionElement): boolean => {
    const constraints = getConstraintsForElement(
      element,
      componentMap,
      plugins
    );

    return (
      constraints.pinToParent ||
      constraints.ancestors.length > 0 ||
      constraints.parent.length > 0 ||
      constraints.descendants.length > 0 ||
      constraints.children.length > 0 ||
      constraints.wraps.length > 0
    );
  };

/**
 * Validates an ordinary move within the current page or component definition
 * without expanding component instances.
 * Returns null when the move needs the full validation pipeline.
 */
export function tryValidateNavigatorMoveFastPath(
  liveComponentMap: Component.ComponentMap,
  validationContext: ValidationContext,
  placement: {
    anchorElement: ExpressionElement;
    anchorPosition: RelativePositionPlacementOptions;
  }
): Result<string, ExpressionElement> | null {
  const {
    collections,
    currentComponentType,
    pages,
    rawParent,
    rawChild,
    plugins,
    currentPageId,
    runtimeArgs,
    featureFlags,
  } = validationContext;

  // When we are adding new elements (alt-drag to copy) resolve full validation
  if (validationContext.isNewElement) {
    return null;
  }

  const currentComponent = liveComponentMap.get(currentComponentType);
  if (!currentComponent) {
    return null;
  }

  const currentComponentRender = Component.getRender(currentComponent);
  // The dragged element.
  const sourceElement = Expressions.getElementById(
    currentComponentRender,
    rawChild.id
  );
  // The dragged element's current parent.
  const sourceParent = Expressions.getParent(
    currentComponentRender,
    rawChild.id
  );
  // The parent that will receive the dragged element.
  const targetParent = Expressions.getElementById(
    currentComponentRender,
    rawParent.id
  );
  // The element the drop is positioned relative to.
  const anchorElement = Expressions.getElementById(
    currentComponentRender,
    placement.anchorElement.id
  );
  const targetPage = pages.get(currentPageId);
  if (
    !sourceElement ||
    !sourceParent ||
    !targetParent ||
    !anchorElement ||
    !targetPage
  ) {
    return null;
  }

  // A self-anchored Navigator move leaves the component definition unchanged.
  if (sourceElement.id === anchorElement.id) {
    return Ok(sourceElement);
  }

  const isEditingComponentDefinition = isUserComponent(currentComponentType);

  // Placement checks only need the target's existing ancestry and component metadata.
  if (
    runtimeArgs.runtimeEnvironment === WebflowEnvironments.DESIGNER &&
    runtimeArgs.isInPageBuildMode
  ) {
    const buildModeValidation = validateBuildModePlacement(
      liveComponentMap,
      liveComponentMap,
      targetParent,
      sourceElement,
      currentComponentType,
      runtimeArgs,
      validationContext.isNewElement
    ).chain(() =>
      validateSlotRestrictions(
        liveComponentMap,
        plugins,
        targetParent,
        sourceElement,
        validationContext.anchorPath,
        runtimeArgs,
        {
          libraryPluginIds: validationContext.libraryPluginIds,
          onSlotRestrictionBlocked: validationContext.onSlotRestrictionBlocked,
        }
      )
    );
    if (getErrorMessage(buildModeValidation)) {
      return buildModeValidation.map(() => sourceElement);
    }
  }

  if (isComponentOnlyDropTarget(targetParent)) {
    if (!isComponent(liveComponentMap)(sourceElement)) {
      return Err('Only Components are allowed in a Page Slot.');
    }

    // Reordering a Component within the same Page Slot only changes sibling
    // order. Its parent and ancestor validation contexts remain unchanged.
    if (sourceParent.id === targetParent.id) {
      return Ok(sourceElement);
    }
  }

  const needsSlotPlacementValidation = contains(sourceElement, isSlot);

  // Instance contents and Slot overrides need the full validation context.
  if (
    contains(sourceElement, isComponentInst(liveComponentMap)) ||
    (needsSlotPlacementValidation && isEditingComponentDefinition)
  ) {
    return null;
  }

  // If we are moving a collection list (even inside a component) resolve a full validation to check
  // nested collection list counts and other rules related to collection lists
  const targetIsInsideCollectionList = Expressions.closestElement(
    currentComponentRender,
    targetParent.id,
    isDynamoWrapper
  );
  if (
    targetIsInsideCollectionList &&
    countDynamoWrappers(rawChild)(liveComponentMap) > 0
  ) {
    return null;
  }

  const hasConstraints = hasPlacementConstraints(liveComponentMap, plugins);
  const needsRelationshipValidation =
    contains(sourceElement, hasConstraints) ||
    Expressions.closestElement(
      currentComponentRender,
      sourceElement.id,
      hasConstraints
    ) ||
    Expressions.closestElement(
      currentComponentRender,
      targetParent.id,
      hasConstraints
    );

  let relationshipValidation: Result<string, ExpressionElement> =
    Ok(sourceElement);
  let stagedComponentMap = liveComponentMap;
  if (needsRelationshipValidation || needsSlotPlacementValidation) {
    const stagedComponentDefinition = addElementAtAnchor(
      removeElementById(currentComponentRender, sourceElement.id),
      sourceElement,
      anchorElement,
      placement.anchorPosition
    );

    if (
      !Expressions.getElementById(stagedComponentDefinition, sourceElement.id)
    ) {
      return null;
    }

    if (needsRelationshipValidation) {
      relationshipValidation = DesignerErrorCodes.errorCodeToMessage(
        DesignerErrorCodes.getMoveMessage
      )(
        validateComponentDefinitionConstraintChanges(
          collections,
          targetPage,
          liveComponentMap,
          plugins,
          currentComponentType,
          currentComponent,
          currentComponentRender,
          stagedComponentDefinition
        )
      ).map(() => sourceElement);
    }

    if (needsSlotPlacementValidation) {
      stagedComponentMap = liveComponentMap.set(
        currentComponentType,
        Component.update(currentComponent, {render: stagedComponentDefinition})
      );
    }
  }

  // Run nesting validations on a non-expanded tree
  return relationshipValidation
    .chain(() =>
      validateNesting(
        plugins,
        [],
        liveComponentMap,
        rawParent,
        featureFlags,
        isEditingComponentDefinition,
        rawChild,
        currentComponent
      )
    )
    .chain((element) =>
      validateDynamicContext({
        target: {
          elementId: rawParent.id,
          component: currentComponent,
          componentMap: liveComponentMap,
          page: targetPage,
          collections,
        },
        source: {
          elementId: rawChild.id,
          component: currentComponent,
          componentMap: liveComponentMap,
          page: targetPage,
          collections,
        },
        runtimeEnvironment: runtimeArgs.runtimeEnvironment,
      }).map(() => element)
    )
    .chain((element) =>
      validateLocalesContext({
        targetData: {
          componentMap: liveComponentMap,
          currentComponent,
        },
        sourceData: {
          componentMap: liveComponentMap,
          currentComponent,
        },
        targetElementId: rawParent.id,
        sourceElementId: rawChild.id,
      }).map(() => element)
    )
    .chain((element) =>
      needsSlotPlacementValidation
        ? validateSlotPlacementInCurrentComponent(
            currentComponentType,
            stagedComponentMap,
            plugins
          ).map(() => element)
        : Ok(element)
    );
}

/**
 * Runs all necessary validations to check if the changes are valid.
 *
 * @param liveComponentMap - The current live component map.
 * @param stagedComponentMap - The staged component map with the changes.
 * @param validationContext - The context for the validation.
 * @param messageGetter - The function to get the error message from the error code.
 * @returns The result of the validation. If the validation is successful, it returns the child element. Otherwise, it returns the error message.
 */
export function validateChanges(
  liveComponentMap: Component.ComponentMap,
  stagedComponentMap: Component.ComponentMap,
  validationContext: ValidationContext,
  messageGetter: DesignerErrorCodes.MessageGetter
): Result<string, ExpressionElement> {
  const {
    collections,
    componentInstanceCounts,
    currentComponentType,
    userComponentUsageMap,
    pages,
    onSlotRestrictionBlocked,
    rawParent,
    rawChild,
    anchorPath,
    isNewElement,
    sourceData,
    plugins,
    currentPageId,
    runtimeArgs,
    featureFlags,
    libraryPluginIds,
  } = validationContext;
  const targetPage = pages.get(currentPageId);
  const sourcePage = sourceData
    ? sourceData.pages.get(sourceData.currentPageId)
    : targetPage;
  const currentComponent = liveComponentMap.get(currentComponentType);
  if (!currentComponent) {
    return Err('Could not find current component from component map.');
  }
  const sourceCurrentComponent = sourceData
    ? sourceData.componentMap.get(sourceData.currentComponentType)
    : currentComponent;
  if (!sourceCurrentComponent) {
    return Err('Could not find source component from component map.');
  }

  const maybeErrorMessage = getErrorMessage(
    validateComponentMapCycles(stagedComponentMap, messageGetter)
  );

  if (maybeErrorMessage) {
    return Err(maybeErrorMessage);
  }

  const cmsRtfError = validateCmsRtfNesting({
    child: rawChild,
    componentMap: liveComponentMap,
    currentComponentType,
    cmsRtfComponentIds: validationContext.cmsRtfComponentIds,
  });
  if (cmsRtfError) {
    return cmsRtfError;
  }

  return DesignerErrorCodes.errorCodeToMessage(messageGetter)(
    validateComponentMapConstraints(
      collections,
      targetPage,
      liveComponentMap,
      stagedComponentMap,
      plugins
    )
  )
    .chain(() => {
      const constrainingUserComponents = detectConstrainingUserComponents({
        componentMap: liveComponentMap,
        currentComponentType,
        currentPageId,
        plugins,
        userComponentUsageMap,
      });

      return validateNesting(
        plugins,
        constrainingUserComponents,
        stagedComponentMap,
        rawParent,
        {
          isComponentSlotDefaultsEnabled:
            featureFlags.isComponentSlotDefaultsEnabled,
        },
        isUserComponent(currentComponentType),
        rawChild,
        currentComponent
      );
    })

    .chain((element: ExpressionElement) => {
      return checkUtilityPagesRestrictions(
        pages,
        liveComponentMap,
        componentInstanceCounts,
        currentComponentType,
        currentPageId,
        rawChild
      ).map(() => element);
    })
    .chain((element: ExpressionElement) =>
      validateDynamicContext({
        target: {
          elementId: rawParent.id,
          component: currentComponent,
          componentMap: liveComponentMap,
          page: targetPage,
          collections,
        },
        source: {
          elementId: rawChild.id,
          component: sourceCurrentComponent,
          componentMap: sourceData?.componentMap ?? liveComponentMap,
          page: sourcePage,
          collections: sourceData?.collections ?? collections,
        },
        runtimeEnvironment: runtimeArgs.runtimeEnvironment,
      }).map(() => element)
    )
    .chain((element: ExpressionElement) => {
      return validateLocalesContext({
        targetData: {
          componentMap: liveComponentMap,
          currentComponent,
        },
        sourceData: sourceData
          ? {
              componentMap: sourceData.componentMap,
              currentComponent: sourceCurrentComponent,
            }
          : {
              componentMap: liveComponentMap,
              currentComponent,
            },
        targetElementId: rawParent.id,
        sourceElementId: rawChild.id,
      }).map(() => element);
    })
    .chain((element: ExpressionElement) => {
      return validateSlotPlacementInCurrentComponent(
        currentComponentType,
        stagedComponentMap,
        plugins
      ).map(() => element);
    })
    .chain(
      targetPage
        ? validatePlacementOnPage(liveComponentMap)(plugins)(targetPage)
        : Ok
    )
    .chain((element: ExpressionElement) => {
      return validateBuildModePlacement(
        liveComponentMap,
        stagedComponentMap,
        rawParent,
        rawChild,
        currentComponentType,
        runtimeArgs,
        isNewElement
      ).map(() => element);
    })
    .chain((element: ExpressionElement) => {
      return validateSlotRestrictions(
        liveComponentMap,
        plugins,
        rawParent,
        rawChild,
        anchorPath,
        runtimeArgs,
        {
          libraryPluginIds,
          onSlotRestrictionBlocked,
        }
      ).map(() => element);
    })
    .chain(
      limitNestedCollectionListCount(
        stagedComponentMap,
        validationContext.dynListLimits
      )
    )
    .chain(
      limitCollectionListCount(
        liveComponentMap,
        validationContext.dynListLimits,
        isNewElement
      )
    );
}

function validateDynamicContext(
  opts: Parameters<typeof internalValidateDynamicContext>[0]
): Result<string, string> {
  const result = internalValidateDynamicContext(opts);
  return result.valid ? Ok(opts.source.elementId) : Err(result.errorMessage);
}

const treeContainsDynamicContent = expressionContainsSpecificElement(
  (el) => isDynamoWrapper(el) || isDynamicBoundOrConditioned(el)
);

/**
 * Validates if the child element can be placed on the page.
 */
const validatePlacementOnPage =
  (componentMap: Component.ComponentMap) =>
  (plugins: Plugins.PluginsType) =>
  (page: PageRecordType) =>
  (rawChild: ExpressionElement) => {
    const pageConstraintErrors = validatePageConstraints(page)(componentMap)(
      rawChild,
      plugins
    );

    if (pageConstraintErrors) {
      return pageConstraintErrors;
    }

    if (isComponentOnlyDropTarget(rawChild) && isDynPage(page)) {
      return Err(`Page slots are not allowed in ${page.name} page.`);
    }

    if (
      isStaticUtilityPage(page) &&
      treeContainsDynamicContent(EElement(rawChild))(componentMap)
    ) {
      return Err(`Dynamic elements are not allowed in ${page.name} page.`);
    }

    return Ok(rawChild);
  };

/**
 * Don't allow collection lists inside components in Utility Pages
 */
function checkUtilityPagesRestrictions(
  pages: IMap<string, PageRecordType>,
  liveComponentMap: Component.ComponentMap,
  componentInstanceCounts: ComponentInstanceCounts,
  currentComponentType: Plugins.QualifiedElement,
  currentPageId: string,
  child: ExpressionElement
) {
  if (
    namesEqual(pageComponentType, currentComponentType) ||
    countDynamoWrappers(child)(liveComponentMap) === 0
  ) {
    return Ok(child);
  }

  const currentComponentId = String(getName(currentComponentType));

  const count = componentInstanceCountsByPage(liveComponentMap)(
    componentInstanceCounts
  )(currentPageId);
  const ancestorComponents =
    getAncestorComponents(liveComponentMap)(currentComponentId);

  // page ids that have instances of the component
  const pagesWithInstances: Set<string> = new Set(
    Object.keys(count[currentComponentId] ?? {})
  );

  // include pages that have instances of component's ancestors
  for (const ancestorComponentId of ancestorComponents) {
    const ancestorCounts = count[ancestorComponentId] ?? {};
    for (const pageId in ancestorCounts) {
      pagesWithInstances.add(pageId);
    }
  }

  // filter for utility pages
  const staticUtilityPagesWithComponent: PageRecordType[] = [];
  pagesWithInstances.forEach((pageId) => {
    const page = pages.get(pageId);
    if (page && isStaticUtilityPage(page)) {
      staticUtilityPagesWithComponent.push(page);
    }
  });

  // if we have a component instance in a utility page, return an error
  if (staticUtilityPagesWithComponent.length > 0) {
    const pageNamesString = staticUtilityPagesWithComponent.reduce(
      (acc, page, index) => {
        const pageName = page && page.name;
        if (pageName) {
          if (acc === '') {
            return pageName;
          } else if (index === staticUtilityPagesWithComponent.length - 1) {
            return `${acc} or ${pageName}`;
          } else {
            return `${acc}, ${pageName}`;
          }
        } else {
          return acc;
        }
      },
      ''
    );
    return Err(
      `Sorry, you can’t add a Collection List to a Component that's used on ${pageNamesString} page.`
    );
  }

  return Ok(child);
}

/**
 * Validates slot placement within the current component
 *
 * Prevents slots from being placed into the page component or
 * inside invalid slot ancestors in the component being edited.
 *
 * We run these validations separately from validateComponentMapConstraints
 * because we only want to validate slot placement within the context of
 * the component the slot belongs to. validateComponentMapConstraints validates
 * element placement across component boundaries.
 */
function validateSlotPlacementInCurrentComponent(
  currentComponentType: Plugins.QualifiedElement,
  stagedComponentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
) {
  const component = stagedComponentMap.get(currentComponentType);

  if (!component) {
    return Err('Could not find current component from staged component map.');
  }

  const renderExpr = Component.getRender(component);
  const slots = getComponentSlots(component);

  // if the component has no slots, we can skip further validations
  if (slots.length > 0) {
    if (namesEqual(pageComponentType, currentComponentType)) {
      return Err('Slots can only be placed in components.');
    }

    for (const slotElement of slots) {
      const slotParentElement = Expressions.getParent(
        renderExpr,
        slotElement.id
      );

      if (!slotParentElement) {
        return Err('Slot can not be placed inside this element.');
      }

      // determine if slot is has an invalid ancestor
      const closestInvalidAncestor = Expressions.closestElement(
        renderExpr,
        slotParentElement.id,
        (ancestor) => !isValidSlotAncestor(ancestor, stagedComponentMap)
      );

      if (closestInvalidAncestor) {
        const displayName = Expressions.getStringFromDisplayName(
          getDisplayNameForType(
            closestInvalidAncestor.type,
            stagedComponentMap,
            plugins
          ),
          {element: closestInvalidAncestor}
        );
        return Err(`Slots can not be placed inside ${displayName}.`);
      }
    }
  }

  return Ok('ok');
}

/**
 * Validates element placement in Page Build mode.
 * Rich text descendants can be moved around inside rich text elements.
 * Components can be added only to Page Slots or Rich Text elements.
 */
function validateBuildModePlacement(
  liveComponentMap: Component.ComponentMap,
  stagedComponentMap: Component.ComponentMap,
  rawParent: ExpressionElement,
  rawChild: ExpressionElement,
  currentComponentType: Plugins.QualifiedElement,
  runtimeArgs: RuntimeArgs,
  isNewElement: boolean
) {
  if (
    runtimeArgs.runtimeEnvironment === WebflowEnvironments.DESIGNER &&
    runtimeArgs.isInPageBuildMode
  ) {
    const parentIsRichText =
      runtimeArgs.canAddComponentsInStaticRichText && isRichText(rawParent);
    const isChildAComponent = isComponentInst(stagedComponentMap)(rawChild);

    // If the element is a rich text child and not a component, validate it as ok.
    if (!isChildAComponent && parentIsRichText) {
      return Ok('ok');
    }

    // If a non-component was somehow added in build mode, throw an error.
    if (!isChildAComponent) {
      if (runtimeArgs.canAddComponentsInStaticRichText) {
        return Err(
          'Only components can be used and must be placed in page slots or rich text when in build mode.'
        );
      }
      return Err(
        'Only components can be used and must be placed in page slots when in build mode.'
      );
    }

    const isValidComponent = canUseComponentAndDescendantsInPageBuildMode({
      components: stagedComponentMap,
      element: rawChild,
    });

    // If a workspace library component that has not been added to the site data yet was somehow used
    if (!isValidComponent) {
      return Err('This component cannot be used in build mode.');
    }

    const stagedCurrentComponent = stagedComponentMap.get(currentComponentType);
    const pageRender =
      stagedCurrentComponent && Component.getRender(stagedCurrentComponent);

    // Paste validates the uncloned clipboard element, so an insert can
    // carry an id that is still live in this Page Slot.
    const liveCurrentComponent = liveComponentMap.get(currentComponentType);
    const livePageRender = isNewElement
      ? undefined
      : liveCurrentComponent && Component.getRender(liveCurrentComponent);

    if (runtimeArgs.isInCampaignBuildMode) {
      const campaignBuildModePlacement = validateCampaignBuildModePlacement({
        stagedComponentMap,
        rawParent,
        rawChild,
        pageRender,
        livePageRender,
      });

      if (getErrorMessage(campaignBuildModePlacement)) {
        return campaignBuildModePlacement;
      }
    }

    const hasPageSlotAsAnAncestor =
      pageRender && isComponentOnlyPageSlotDescendant(pageRender, rawParent);

    if (runtimeArgs.canAddComponentsInStaticRichText) {
      // If the component is valid but is not being placed into a page slot or rich text
      if (!hasPageSlotAsAnAncestor && !parentIsRichText) {
        return Err('Component can only be placed in a page slot or rich text.');
      }
    } else {
      // If the component is valid but is not being placed into a page slot
      if (!hasPageSlotAsAnAncestor) {
        return Err('Component can only be placed in a page slot.');
      }
    }

    // If the component or any of its children (in Slots) are stale
    if (
      isUserComponentInst(liveComponentMap, rawChild) &&
      isOrContainsStaleUserComponent(
        rawChild,
        stagedComponentMap,
        runtimeArgs.staleComponents
      )
    ) {
      return Err(
        designerContent['designer.components.disabled-stale-component']
      );
    }
  }
  return Ok('ok');
}

/**
 * The restriction toast carries a CTA, so two designer-flux call sites have to
 * recognize a slot-restriction rejection -- but `validateChanges` only hands
 * back a string. Producing and detecting the message from one place keeps them
 * from drifting: matching the rendered copy at each call site means a copy edit
 * (or per-locale translations, which `designerTranslation` warns it does not yet
 * do) silently drops the CTA with nothing failing.
 */
export const slotRestrictionErrorMessage = (componentName: string): string =>
  designerTranslation.t('designer.components.slot-restriction-error', {
    componentName,
  });

export const isSlotRestrictionError = (message: string): boolean =>
  message.endsWith(slotRestrictionErrorMessage(''));

/**
 * Whether an element is subject to slot restrictions: the site's own
 * components and an installed library's plugin components. A core plugin's
 * components are never candidates -- the picker does not offer those, so
 * restricting against one would refuse it from every slot with no way for an
 * author to permit it.
 *
 * Shared by validateSlotRestrictions and the drag-cursor check in
 * DragDropUtils so the cursor and the drop cannot disagree.
 */
export const isSlotRestrictionCandidate = (
  liveComponentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  rawChild: ExpressionElement,
  libraryPluginIds: ReadonlySet<string> | undefined
): boolean =>
  isUserComponentInst(liveComponentMap, rawChild) ||
  (Plugins.isPluginComponentInst(plugins, rawChild) &&
    Boolean(libraryPluginIds?.has(rawChild.type[0])));

function validateSlotRestrictions(
  liveComponentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  rawParent: ExpressionElement,
  rawChild: ExpressionElement,
  anchorPath: ExpressionPath | null | undefined,
  runtimeArgs: RuntimeArgs,
  {
    libraryPluginIds,
    onSlotRestrictionBlocked,
  }: {
    libraryPluginIds: ReadonlySet<string> | undefined;
    onSlotRestrictionBlocked?: (ctx: {
      parentId: string;
      slotId: string;
      attemptedComponentId: string;
    }) => void;
  }
): Result<string, string> {
  // Checked before the candidate test below: this runs on every validated
  // change, including the multiplayer server path and each drag-over tick,
  // and the component lookups are not free.
  if (
    runtimeArgs.runtimeEnvironment !== WebflowEnvironments.DESIGNER ||
    !runtimeArgs.isInPageBuildMode
  ) {
    return Ok('ok');
  }

  // Both a __SitePlugin instance, including the local copy of a library
  // component, and a library component instance are candidates.
  //
  // A library component placed before its local copy exists is still a
  // component instance, but a plugin-qualified one: localization to a local
  // copy happens at commit time, after this runs.
  if (
    !isSlotRestrictionCandidate(
      liveComponentMap,
      plugins,
      rawChild,
      libraryPluginIds
    )
  ) {
    return Ok('ok');
  }

  const childComponentId = `${rawChild.type[0]}::${rawChild.type[1]}`;

  const blockedMessage = (
    parentType: Plugins.QualifiedElement | undefined,
    fieldId: string
  ) => {
    // A not-yet-materialized library component is absent from the live map, so
    // fall back to the plugin definition -- otherwise the toast names no
    // component at all.
    const childComp =
      liveComponentMap.get(rawChild.type) ??
      Plugins.getPluginComponent(plugins, rawChild);
    if (parentType) {
      onSlotRestrictionBlocked?.({
        parentId: `${parentType[0]}::${parentType[1]}`,
        slotId: fieldId,
        attemptedComponentId: childComponentId,
      });
    }
    return Err(
      slotRestrictionErrorMessage(
        childComp ? Component.getDisplayName(childComp) : ''
      )
    );
  };

  // Preferred path: anchorPath names the exact SlotContent field being filled
  // on the parent instance. Both paste and drop compute it, and it is the only
  // signal that identifies a single slot -- rawParent alone is the instance,
  // which may own several slots with different allow-lists.
  const anchorStep = anchorPath?.[0];
  if (
    anchorStep &&
    anchorStep.in === 'Record' &&
    isUserComponentInst(liveComponentMap, rawParent)
  ) {
    const parentComponent = liveComponentMap.get(rawParent.type);
    if (!parentComponent) return Ok('ok');
    const restrictions = getSlotRestrictions(
      parentComponent,
      anchorStep.at,
      liveComponentMap
    );
    if (!restrictions) return Ok('ok');
    return isComponentAllowedInSlot(
      restrictions,
      childComponentId,
      liveComponentMap
    )
      ? Ok('ok')
      : blockedMessage(rawParent.type, anchorStep.at);
  }

  // Path A: rawParent is a Slot element (user is inside the component definition)
  if (isSlot(rawParent)) {
    const fieldId = getSlotOverrideFieldId(rawParent);
    if (!fieldId) return Ok('ok');

    // Resolve the Slot's owner from the Slot itself. Walking up to a component
    // root only works when the Slot is reachable from the render the caller is
    // looking at -- and it is not on the paste path, where selection stays page
    // level while the Slot lives in the instance's definition.
    const ownerComponent = getComponentBySlotElementId(liveComponentMap).get(
      rawParent.id
    );
    if (!ownerComponent) return Ok('ok');

    const restrictions = getSlotRestrictions(
      ownerComponent,
      fieldId,
      liveComponentMap
    );
    if (!restrictions) return Ok('ok');

    if (
      !isComponentAllowedInSlot(
        restrictions,
        childComponentId,
        liveComponentMap
      )
    ) {
      let ownerType: Plugins.QualifiedElement | undefined;
      liveComponentMap.find((component, type) => {
        if (component !== ownerComponent) return false;
        ownerType = type;
        return true;
      });
      return blockedMessage(ownerType, fieldId);
    }
    return Ok('ok');
  }

  // Path B: rawParent is a component instance and nothing named the field. This
  // is the ambiguous case -- slot content lives in the instance's own data
  // record and getParent skips Record steps, so dropping a sibling next to
  // existing slot content resolves the parent to the instance while
  // getTargetedSlotAnchorPath returns null.
  //
  // We cannot tell which slot was targeted here, so refuse whenever any slot on
  // the component would reject the child. Reading it the other way round --
  // "some slot is unrestricted, so allow" -- admitted a disallowed component
  // into a restricted slot. The anchorPath branch above is what resolves the
  // precise case; callers that supply it never reach this.
  if (isUserComponentInst(liveComponentMap, rawParent)) {
    const parentComponent = liveComponentMap.get(rawParent.type);
    if (!parentComponent) return Ok('ok');

    const fields = Types.getRecordFields(
      Component.getDataType(parentComponent)
    );
    if (!fields) return Ok('ok');

    for (const fieldId of Object.keys(fields)) {
      const fieldType = fields[fieldId];
      if (!fieldType || !Types.isSlotContentType(fieldType)) continue;

      const restrictions = getSlotRestrictions(
        parentComponent,
        fieldId,
        liveComponentMap
      );
      if (
        restrictions &&
        !isComponentAllowedInSlot(
          restrictions,
          childComponentId,
          liveComponentMap
        )
      ) {
        return blockedMessage(rawParent.type, fieldId);
      }
    }
  }

  return Ok('ok');
}

function isValidSlotAncestor(
  element: ExpressionElement,
  componentMap: Component.ComponentMap
): boolean {
  return (
    // restrict slot placement within components to these elements
    isSection(element) ||
    isDivBlock(element) ||
    isList(element) ||
    isListItem(element) ||
    isDropdownList(element) ||
    isDropdownToggle(element) ||
    isDropdownWrapper(element) ||
    isContainer(element) ||
    isBlockContainer(element) ||
    isCell(element) ||
    isColumn(element) ||
    isLayout(element) ||
    isRow(element) ||
    isGrid(element) ||
    isHFlex(element) ||
    isVFlex(element) ||
    isSliderWrapper(element) ||
    isSliderMask(element) ||
    isSliderSlide(element) ||
    isTabsWrapper(element) ||
    isTabsContent(element) ||
    isTabsPane(element) ||
    isNavbarWrapper(element) ||
    isNavbarContainer(element) ||
    isNavbarMenu(element) ||
    isDOM(element) ||
    // AI code components nest slot props directly under their Code Island
    isCodeIsland(element) ||
    // and also allow placing slots into slot of nested component instances
    isUserComponentInst(componentMap, element)
  );
}

const forTesting = {
  validatePlacementOnPage,
  validateDynamicContext,
};

export {forTesting};
